// Monetary values are represented as integer cents everywhere in the app.
// Keep parsing string-based so a user-entered decimal never passes through a
// floating-point number before it is stored.

/** Ask for an explicit confirmation at or above $1.000.000,00. */
export const ARS_UNUSUAL_AMOUNT_CENTS = 100_000_000;

/**
 * The current database columns are PostgreSQL `integer`s. Keep an operational
 * ceiling below their 2_147_483_647-cent limit so the API can return a clear
 * validation error instead of an overflow from the database.
 */
export const ARS_MAX_AMOUNT_CENTS = 2_000_000_000;

export type ArsInputErrorCode =
  | "empty"
  | "invalid_characters"
  | "invalid_format"
  | "too_many_decimals"
  | "out_of_range";

export type ParseArsInputResult =
  | { ok: true; cents: number }
  | { ok: false; code: ArsInputErrorCode; message: string };

export type ArsAmountPolicy = "normal" | "confirmation_required" | "blocked";

const INTEGER_GROUPED = /^\d{1,3}(?:\.\d{3})*$/;
const INTEGER_PLAIN = /^\d+$/;

function parseIntegerCents(integerPart: string, decimalPart: string): ParseArsInputResult {
  const normalizedInteger = integerPart.replace(/\./g, "").replace(/^0+(?=\d)/, "") || "0";
  const normalizedDecimal = decimalPart.padEnd(2, "0");
  const centsAsText = `${normalizedInteger}${normalizedDecimal}`;

  // Number is only used after the exact string representation has been built.
  // Rejecting unsafe values prevents a silent loss of cents.
  const cents = Number(centsAsText);
  if (!Number.isSafeInteger(cents)) {
    return { ok: false, code: "out_of_range", message: "El importe es demasiado grande" };
  }

  return { ok: true, cents };
}

/**
 * Parse Argentine peso input exactly into cents.
 *
 * Accepted examples: 8421,16 / 8.421,16 / 8421.16 / $ 8.421,16.
 * A lone dot followed by exactly three digits is a thousands separator, so
 * `8.421` means $8.421,00 (not $8,421).
 */
export function parseArsInput(input: string): ParseArsInputResult {
  const compact = input.trim().replace(/\s+/g, "");
  if (!compact) return { ok: false, code: "empty", message: "Ingresá un importe" };

  const value = compact.startsWith("$") ? compact.slice(1) : compact;
  if (!value) return { ok: false, code: "empty", message: "Ingresá un importe" };
  if (!/^[\d.,]+$/.test(value)) {
    return { ok: false, code: "invalid_characters", message: "Solo números, puntos o comas" };
  }

  const commas = (value.match(/,/g) ?? []).length;
  const dots = (value.match(/\./g) ?? []).length;
  if (commas > 1) {
    return { ok: false, code: "invalid_format", message: "Formato de importe inválido" };
  }

  let integerPart = value;
  let decimalPart = "";

  if (commas === 1) {
    const [integer, decimal] = value.split(",");
    if (!integer || !decimal) {
      return { ok: false, code: "invalid_format", message: "Formato de importe inválido" };
    }
    if (!/^\d+$/.test(decimal)) {
      return { ok: false, code: "invalid_format", message: "Formato de importe inválido" };
    }
    if (decimal.length > 2) {
      return { ok: false, code: "too_many_decimals", message: "El importe admite hasta dos decimales" };
    }
    if (!/^\d{1,2}$/.test(decimal) || !(INTEGER_PLAIN.test(integer) || INTEGER_GROUPED.test(integer))) {
      return { ok: false, code: "invalid_format", message: "Formato de importe inválido" };
    }
    integerPart = integer;
    decimalPart = decimal;
  } else if (dots === 0) {
    if (!INTEGER_PLAIN.test(value)) {
      return { ok: false, code: "invalid_format", message: "Formato de importe inválido" };
    }
  } else if (dots === 1) {
    const [integer, suffix] = value.split(".");
    if (!integer || !suffix) {
      return { ok: false, code: "invalid_format", message: "Formato de importe inválido" };
    }

    if (suffix.length === 1 || suffix.length === 2) {
      if (!INTEGER_PLAIN.test(integer)) {
        return { ok: false, code: "invalid_format", message: "Formato de importe inválido" };
      }
      integerPart = integer;
      decimalPart = suffix;
    } else if (suffix.length === 3 && INTEGER_GROUPED.test(value)) {
      // One dot with a three-digit suffix is the agreed thousands notation.
      integerPart = value;
    } else if (suffix.length > 2) {
      return { ok: false, code: "too_many_decimals", message: "El importe admite hasta dos decimales" };
    } else {
      return { ok: false, code: "invalid_format", message: "Formato de importe inválido" };
    }
  } else if (INTEGER_GROUPED.test(value)) {
    integerPart = value;
  } else {
    return { ok: false, code: "invalid_format", message: "Formato de importe inválido" };
  }

  return parseIntegerCents(integerPart, decimalPart);
}

/** Returns the local input policy for a parsed amount. */
export function getArsAmountPolicy(cents: number): ArsAmountPolicy {
  if (!Number.isSafeInteger(cents) || cents < 0 || cents > ARS_MAX_AMOUNT_CENTS) return "blocked";
  if (cents >= ARS_UNUSUAL_AMOUNT_CENTS) return "confirmation_required";
  return "normal";
}

/** Format integer cents as Argentine pesos with exactly two decimal digits. */
export function formatARSExact(cents: number): string {
  if (!Number.isSafeInteger(cents)) return "—";

  const sign = cents < 0 ? "−" : "";
  const absolute = Math.abs(cents);
  const centsText = String(absolute).padStart(3, "0");
  const decimal = centsText.slice(-2);
  const integer = centsText.slice(0, -2);
  const groupedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}$ ${groupedInteger},${decimal}`;
}

/**
 * Existing compact-display formatter. The exact formatter is used whenever an
 * operator is about to persist an amount; compact views may omit cents.
 */
export function formatARS(cents: number, withCents = false): string {
  if (withCents) return formatARSExact(cents);
  if (!Number.isSafeInteger(cents)) return "—";

  const sign = cents < 0 ? "−" : "";
  const roundedPesos = Math.round(Math.abs(cents) / 100);
  const groupedPesos = String(roundedPesos).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}$ ${groupedPesos}`;
}

/**
 * Legacy convenience wrapper for existing forms. New code should use
 * `parseArsInput` and handle its explicit error result instead of treating an
 * invalid value as zero.
 */
export function centsFromInput(input: string): number {
  const result = parseArsInput(input);
  return result.ok ? result.cents : 0;
}

/**
 * Format integer cents as an editable input value (no currency symbol, comma
 * as decimal separator). Round-trips exactly with `parseArsInput`.
 */
export function inputValueFromCents(cents: number): string {
  if (!Number.isSafeInteger(cents) || cents < 0) return "";
  const centsText = String(cents).padStart(3, "0");
  const integer = centsText.slice(0, -2);
  const decimal = centsText.slice(-2);
  return decimal === "00" ? integer : `${integer},${decimal}`;
}
